import type { NuxtApp } from '#app';
import { stringifyQuery } from 'vue-router';

type TComponent = Record<string, unknown>;

type TExclusions = Readonly<Partial<{
  pathQueryKeys: ReadonlyArray<string>,
}>>;

type TOptions = Readonly<Partial<{
  exclusions: TExclusions,
  expirationDate: TDate,
  initialValue: unknown,
  validateFunction: (value: unknown) => boolean,
  onInvalid: () => void,
}>>;

const getPath = ({ exclusions }: TOptions): string => {
  const route = useRoute();
  const excludedQueryKeys = exclusions?.pathQueryKeys;
  if (!excludedQueryKeys?.length) {
    return route.fullPath;
  }

  const filteredQuery = Object.fromEntries(
    Object.entries(route.query)
      .filter(([key]) => !excludedQueryKeys.includes(key)),
  );
  const queryString = stringifyQuery(filteredQuery);
  return queryString ? `${route.path}?${queryString}` : route.path;
};

const setValue = (
  component: TComponent,
  key: string,
  value: unknown,
  { validateFunction, onInvalid }: TOptions,
) => {
  if (!validateFunction || validateFunction(value)) {
    component[key] = value;
  } else {
    onInvalid?.();
  }
};

const retrieveUserStorageFromDatabase = async (
  app: NuxtApp,
  component: TComponent,
  userId: TUuid,
  key: string,
  options: TOptions,
) => {
  try {
    const response = await app.$cms.request(
      app.$readItems('user_data_storage', {
        filter: {
          user: { _eq: userId },
          path: { _eq: getPath(options) },
          key: { _eq: key },
          _or: [
            { valid_until: { _gte: new Date() } },
            { valid_until: { _null: true } },
          ],
        },
        fields: ['id', 'key', 'value'],
      }),
    );
    if (response.length > 0) {
      const value = JSON.parse(response[0].value);
      setValue(component, key, value, options);
    }
  } catch (error) {
    console.error('Error retrieving user storage from database:', error);
  }
};

const saveUserStorageToDatabase = async (
  app: NuxtApp,
  userId: TUuid,
  key: string,
  value: unknown,
  options: TOptions,
) => {
  const path = getPath(options);
  const existingRecords = await app.$cms.request(
    app.$readItems('user_data_storage', {
      filter: {
        key: { _eq: key },
        user: { _eq: userId },
        path: { _eq: path },
      },
    }),
  );
  try {
    const payload = {
      key,
      value: JSON.stringify(value),
      user: userId,
      valid_until: options.expirationDate || null,
      path: path,
    };

    if (existingRecords?.length) {
      const recordId = existingRecords[0].id;
      await app.$cms.request(app.$updateItem('user_data_storage', recordId, payload));
    } else {
      await app.$cms.request(app.$createItem('user_data_storage', payload));
    }
  } catch (error) {
    console.error('Error saving user data to database:', error);
  }
};

export const useDatabaseStorage = async (
  component: TComponent,
  key: string,
  options: TOptions = {},
) => {
  const app = useNuxtApp();
  const userId = app.$store?.user?.id;
  if (!userId) {
    console.error('Failed to use database storage: unknown user');
    return;
  }

  const { initialValue } = options;
  const hasInitialValue = initialValue !== undefined;
  if (hasInitialValue) {
    setValue(component, key, initialValue, options);
  } else {
    try {
      await retrieveUserStorageFromDatabase(app, component, userId, key, options);
    } catch (error) {
      console.error('Error loading user data from database:', error);
    }
  }

  watch(
    () => component[key],
    async (newValue) => {
      try {
        await saveUserStorageToDatabase(app, userId, key, newValue, options);
      } catch (error) {
        console.error('Error saving user data to database:', error);
      }
    },
    { deep: true, immediate: hasInitialValue },
  );
};
